import {
	SetMicrosoftServicesPasswordModel,
	setMicrosoftServicesPassword
} from "client/Api/UserProfile/service";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotEmpty } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { WelcomeStep } from "./WelcomeStep/WelcomeStep";
import {
	BusyIndicator,
	Form,
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import image from "Content/Images/404-error.svg";
import { ContentLayout } from "./ContentLayout";

export type VerifyPasswordStepProps = {
	onNext: () => void;
};

export const VerifyPasswordStep = (props: VerifyPasswordStepProps): ReactElement => {
	const {
		onNext
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [attemptsLeft, setAttemptsLeft] = useState<number>(MAX_AMOUNT_OF_ATTEMPTS);
	const [formData, setFormData] = useState<SetMicrosoftServicesPasswordModel>({
		password: ""
	});

	const formHandler: IFormHandler<SetMicrosoftServicesPasswordModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {

				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const wasSuccessful: boolean = await setMicrosoftServicesPassword(
					formData,
					globalCancellation
				);

				if (wasSuccessful) {
					onNext();
					return;
				}

				setAttemptsLeft((x) => x - 1);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			isBusy,
			onNext
		]
	);

	const signOutHandler = useCallback(
		(): void => {
			navigateRelativeToEnvironment("SignOut");
		},
		[]
	);

	const hasAttemptsLeft: boolean = attemptsLeft > 0;

	const explanationKey: LocalizationKey = !hasAttemptsLeft
		? "_CouldntVerifyPasswordSignOutText"
		: attemptsLeft === MAX_AMOUNT_OF_ATTEMPTS
			? "_VerifyPasswordText"
			: "_CouldntVerifyPasswordTryAgainText";

	return (
		<WelcomeStep>
			<WelcomeStep.Left
				trackingId="verify-password-step"
				isVerticallyCentred
			>
				{isBusy && (
					<BusyIndicator />
				)}

				<ContentLayout
					title={localize("_VerifyPasswordCaption")}
					explanation={localize(explanationKey)}
				>
					{hasAttemptsLeft && (
						<Form onSubmit={submitHandler}>
							<FormItem label={localize("_Password")}>
								<TextInput
									{...formHandler.for("password").attachToTextInput()}
									type="password"
									autoFocus="focus"
								/>

								<ValidationError {...formHandler.for("password").attachToValidationError()} />
							</FormItem>
						</Form>
					)}

					<ContentLayout.Buttons
						onContinue={
							hasAttemptsLeft
								? submitHandler
								: signOutHandler
						}
						primaryButtonText={
							hasAttemptsLeft
								? attemptsLeft === MAX_AMOUNT_OF_ATTEMPTS
									? localize("_Verify")
									: `${localize("_Retry")} (${attemptsLeft})`
								: localize("_SignOut")
						}
					/>
				</ContentLayout>
			</WelcomeStep.Left>

			<WelcomeStep.Right
				imageUrl={image}
			/>
		</WelcomeStep>
	);
};
VerifyPasswordStep.displayName = "VerifyPasswordStep";

const MAX_AMOUNT_OF_ATTEMPTS = 3;

const validationConfiguration: IValidationConfiguration<SetMicrosoftServicesPasswordModel> =
	createValidationConfiguration<SetMicrosoftServicesPasswordModel>();

validationConfiguration.forScalar("password")
	.add(
		isNotEmpty()
	);
